import type { ProductDisplayData } from '@eliadmin/core/src/api/products/types/product-types';
import { useMemo } from 'react';
import type { ContentField } from '@eliadmin/core/src/api/content-fields/content-fields-types';
import { getVisibleContentFields } from '@eliadmin/core/src/utils/content-fields';
import {
  getNamedContentTextField,
  omitMappedContentText,
} from '@eliadmin/core/src/api/content-fields/content-text-mapping';

import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { PRODUCT_DETAIL } from '@eliadmin/core/src/utils/dlz/cms-config';
import { stripTags } from '@eliadmin/core/src/utils/dlz/text';

export type Parameter = { id: number; name: string; value: string };
export type TextField = ContentField;

const MEDIA = /<(img|iframe|video|oembed|table)\b/i;

/** Editors leave `<p>&nbsp;</p>` behind: only text or embedded media count as content. */
const hasContent = (html: string) => stripTags(html) !== '' || MEDIA.test(html);

/** Attributes with at least one value as label / value rows (the values joined by a comma). */
function parametersOf(product: ProductDisplayData): Parameter[] {
  return (product.attributes ?? []).flatMap(attribute => {
    const value = (attribute.values ?? [])
      .map(item => item.name)
      .filter(Boolean)
      .join(', ');
    return value ? [{ id: attribute.id, name: attribute.name, value }] : [];
  });
}

/** Text fields of the description column: all but the highlights, which belong to the buy box. */
function textFieldsOf(product: ProductDisplayData): TextField[] {
  const highlightsId = getNamedContentTextField(product, [PRODUCT_DETAIL.highlightsField])?.id;
  return getVisibleContentFields(
    highlightsId === undefined
      ? product
      : omitMappedContentText(product, [
          { groupId: highlightsId, component: 'same-name-text', itemIndex: 0 },
        ]),
  );
}

/**
 * What the „Popis a parametre“ tab shows, with the template settings of the core tabs applied:
 * the description of the selected variant (otherwise of the product), the parameters and the
 * remaining text fields. The tab exists when any of them is not empty.
 */
export function useDetailContent() {
  const settings = useGlobalStore(s => s.templateSettings.product_detail);
  const product = useVariantsStore(s => s.product);
  const selectedVariant = useVariantsStore(s => s.selectedVariant);
  const showDescription = settings?.description?.enabled === true;
  const showParameters = settings?.parameters?.enabled === true;

  return useMemo(() => {
    const html = selectedVariant?.description || product.description || '';
    return {
      description: showDescription && hasContent(html) ? html : '',
      parameters: showParameters ? parametersOf(product) : [],
      textFields: textFieldsOf(product),
    };
  }, [product, selectedVariant?.description, showDescription, showParameters]);
}
